import { glsDecodeToken, glsResolveAccountId, GlsTokenClaims } from '@gls-its-frontends/common/gls-auth';
import { Permission, Role } from '@shipit/shared/access-control/util';
import { AttributeStoreUserData, RolesBySystem, UserShipper, System } from '@shipit/shared/api-shipit/util';
import { CONTACT_ID_WILDCARD } from '@shipit/shared/core/util';
import { defineStore } from 'pinia';
import { computed, ComputedRef, type Ref, ref } from 'vue';

const mainStoreKey: string = 'main';

const mainStore = defineStore(mainStoreKey, () => {
    const accessToken: Ref<string | null> = ref(null);
    const attributeStoreData: Ref<AttributeStoreUserData | null> = ref(null);
    const username: Ref<string | null> = ref(null);
    const permissions: Ref<Permission[]> = ref([]);
    const claims: ComputedRef<GlsTokenClaims | null> = computed((): GlsTokenClaims | null => {
        if (!accessToken.value) {
            return null;
        }

        return glsDecodeToken(accessToken.value);
    });
    const oid: ComputedRef<string | null> = computed((): string | null => {
        if (!claims.value) {
            return null;
        }

        return glsResolveAccountId(claims.value);
    });
    const roles: ComputedRef<Role[]> = computed((): Role[] => {
        const webRoles: RolesBySystem | undefined = attributeStoreData.value?.rolesBySystem.find(
            (rbs: RolesBySystem): boolean => rbs.system === System.WEB
        );

        return webRoles?.roles || [];
    });

    const shippers: ComputedRef<UserShipper[]> = computed((): UserShipper[] => {
        return attributeStoreData.value?.shippers || [];
    });

    const activeShippers: ComputedRef<UserShipper[]> = computed((): UserShipper[] => {
        return shippers.value.filter((shipper: UserShipper): boolean => shipper.isActive && shipper.contactId !== CONTACT_ID_WILDCARD);
    });

    const setAccessToken = (token: string): void => {
        accessToken.value = token;
    };

    const setUsername = (name: string): void => {
        username.value = name;
    };

    const setPermissions = (userPermissions?: Permission[]): void => {
        permissions.value = userPermissions?.length ? [...userPermissions] : [];
    };

    return {
        accessToken,
        setAccessToken,
        attributeStoreData,
        claims,
        oid,
        roles,
        setUsername,
        username,
        permissions,
        setPermissions,
        shippers,
        activeShippers
    };
});

export default mainStore;
